import { DEFAULT_SHIRT, hairArt, isHairstyle, type Shirt } from "../shared/shirts.ts";
import { DRAW_ORDER, cosmetic, cosmeticArt, normalizeAccessories } from "../shared/cosmetics.ts";
import { BADGE_PIN, BADGE_SPRITES, badgePinSpots, type AchievementKey } from "../shared/achievements.ts";

/** New original portrait; the existing mascot remains the fallback for the art flag. */
export const PIXEL_PEON = {
  still: "/ui/peon-happy-v1.png",
  sheet: "/ui/peon-happy-sheet-v1.png",
  size: 64,
  columns: 3,
  rows: 3,
} as const;

export type PeonMood = "happy" | "tired";
export function peonMood(openTasks: number | null): PeonMood {
  return openTasks !== null && Number.isFinite(openTasks) && openTasks < 6 ? "happy" : "tired";
}
/**
 * The sprite and sheet for a mood and a shirt (shared/shirts.ts). "brown" is the art as drawn; every
 * other shirt is a recoloured copy written by scripts/make-shirts.mjs with the colour as a suffix.
 */
export function peonArtwork(mood: PeonMood, shirt: Shirt = DEFAULT_SHIRT) {
  const suffix = shirt === DEFAULT_SHIRT ? "" : `-${shirt}`;
  return { ...PIXEL_PEON, still: `/ui/peon-${mood}-v1${suffix}.png`, sheet: `/ui/peon-${mood}-sheet-v1${suffix}.png` };
}

/**
 * Your peon, as the account sheet shows him (owner, 2026-09-24: "The user peon should be the original
 * low fidelity peon model, not the new animated one. The animated one represents an assistant, not
 * the user."): the mascot, the Peon of the Month's model without the crown, in your shirt. "brown" is
 * the mascot as drawn; every other shirt is a recoloured copy written by scripts/make-shirts.mjs.
 */
export function userPeonArtwork(shirt: Shirt = DEFAULT_SHIRT): string {
  return shirt === DEFAULT_SHIRT ? "/ui/peon-original@3x.png" : `/ui/peon-original-${shirt}@3x.png`;
}

/** The @3x art path's 1x file: drawn at two thirds of a CSS pixel an art pixel, a 3x screen doubles each pixel exactly. */
export function oneXArt(src: string): string {
  return src.replace(/@3x\.png$/, ".png");
}

/**
 * The hair layer to stack over a portrait (owner, 2026-10-04: "You should be able to choose a
 * hairstyle for your peon"): the @3x twin of shared/shirts.ts hairArt, swapped in the way
 * userPeonArtwork and crownedPortraitUrl show the shirt's @3x art. `crowned` picks the layer that
 * lines up with the crowned portrait (the crown's pixels cleared). Null for "none", for a peon who
 * never chose (null or undefined) and for a value this build does not know: nothing is drawn.
 */
export function hairLayerUrl(hair: unknown, crowned: boolean): string | null {
  if (!isHairstyle(hair)) return null;
  const path = hairArt(hair, crowned);
  return path ? path.replace(/\.png$/, "@3x.png") : null;
}

/**
 * The layer for one piece of the wardrobe (shared/cosmetics.ts): the @3x twin of cosmeticArt, the
 * crowned file only where the piece has one (a beard or an accessory is drawn from its plain file
 * under the crown too: both files share the crowned portrait's 46x44 grid). Null for "none", an
 * unknown id, or a piece of another category than `category` when one is asked for.
 */
export function pieceLayerUrl(id: unknown, crowned: boolean, category?: "hair" | "beard" | "accessory"): string | null {
  if (typeof id !== "string" || id === "none") return null;
  const item = cosmetic(id);
  if (!item || (category && item.category !== category)) return null;
  return cosmeticArt(item, crowned).replace(/\.png$/, "@3x.png");
}

/** What a portrait may be dressed in: any of the look's parts, unknown values tolerated (a row from an older server, a piece this build does not know). */
export interface LayeredLook {
  hair?: unknown;
  beard?: unknown;
  accessories?: unknown;
  /** The badges pinned on the chest (pinLayers); never drawn by lookLayers, so the small peon and the stamp wear none. */
  badges?: unknown;
}

/**
 * Every layer a look stacks over the shirt's portrait, back to front (shared/cosmetics.ts
 * DRAW_ORDER): the beard (under the hair at the sideburns), the hair, then the accessories, neck
 * before ears before face. Each is the @3x file; nothing for "none", null or what this build does
 * not know.
 */
export function lookLayers(look: LayeredLook, crowned: boolean): { key: string; src: string }[] {
  const out: { key: string; src: string }[] = [];
  const beard = pieceLayerUrl(look.beard, crowned, "beard");
  if (beard) out.push({ key: "beard", src: beard });
  const hair = hairLayerUrl(look.hair, crowned);
  if (hair) out.push({ key: "hair", src: hair });
  const worn = normalizeAccessories(Array.isArray(look.accessories) ? look.accessories : []);
  for (const slot of DRAW_ORDER) {
    for (const id of worn) {
      if (cosmetic(id)?.slot !== slot) continue;
      const src = pieceLayerUrl(id, crowned, "accessory");
      if (src) out.push({ key: id, src });
    }
  }
  return out;
}

/** The pin sheet (scripts/make-badge-pins.mjs): one 8x8 cell per badge, in sprite order. */
export const BADGE_PINS_ART = "/ui/badge-pins@3x.png";

/** One pin as the web draws it: its box as percentages of the portrait's box, and its cell of the sheet. */
export interface PinLayer {
  key: AchievementKey;
  left: string;
  top: string;
  width: string;
  height: string;
  backgroundSize: string;
  backgroundPosition: string;
}

const pct = (n: number) => `${+(n * 100).toFixed(4)}%`;

/**
 * The badge worn over the shirt's patch (docs/peon-look.md "Badges"; owner, 2026-10-05: "Rather than
 * 3 badges should just be able to set one badge that sits on the peons shirt where the existing
 * emblem is"), the layer kind after the accessories: one 8x8 pin or none (shared/achievements.ts
 * badgePinSpots), placed in the portrait's box. Over the
 * crowned portrait the box is the 46x44 frame; over the mascot the frame sits 2 art pixels lower
 * (`.peon-layer-plain`), so the pins move down with it and stay on the shirt. Under the crown
 * nothing changes.
 */
export function pinLayers(look: LayeredLook, crowned: boolean): PinLayer[] {
  const { frameW, frameH, size } = BADGE_PIN;
  const drop = crowned ? 0 : 2;
  return badgePinSpots(Array.isArray(look.badges) ? look.badges : []).map((p) => ({
    key: p.key,
    left: pct(p.x / frameW),
    top: pct((p.y + drop) / frameH),
    width: pct(size / frameW),
    height: pct(size / frameH),
    backgroundSize: `${BADGE_SPRITES * 100}% 100%`,
    backgroundPosition: `${BADGE_SPRITES > 1 ? pct(p.sprite / (BADGE_SPRITES - 1)) : "0%"} 0`,
  }));
}

export function peonPresentation(originalAssets: boolean, animated: boolean): "pixel-still" | "pixel-animated" | "legacy-still" | "legacy-animated" {
  return originalAssets ? (animated ? "pixel-animated" : "pixel-still") : (animated ? "legacy-animated" : "legacy-still");
}

/** Rows: open, half-blink, closed. Columns: rest, small mouth, open mouth. */
export function pixelPeonCell(blink: number, mouth: number): { x: number; y: number } {
  const cell = (value: number) => Number.isFinite(value) ? Math.max(0, Math.min(2, Math.round(value))) : 0;
  return { x: -cell(mouth) * PIXEL_PEON.size, y: -cell(blink) * PIXEL_PEON.size };
}
